import React, { useEffect, useMemo, useState } from "react";
import { GsdModuleNavItem, useI18n } from "@/coquille-bridge";
import { tApp } from "@/i18n/karateI18n.js";

/**
 * Sidebar avec groupes repliables — BT-025/026 (admin, Sensei, Sempai).
 */
export function KarateGroupedSidebar({
  groups = [],
  findGroupForPath,
  renderLink,
  isItemActive,
  pathname = "",
  title,
  subtitle,
  ariaLabel,
  testId = "karate-grouped-sidebar",
  groupTestIdPrefix = "karate-nav-group",
}) {
  const { lang, t } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const activeGroupId = findGroupForPath ? findGroupForPath(pathname) : null;

  const defaultOpen = useMemo(() => {
    const initial = {};
    for (const g of groups) {
      if (!g.collapsible) continue;
      initial[g.id] = g.id === activeGroupId;
    }
    return initial;
  }, [groups, activeGroupId]);

  const [openGroups, setOpenGroups] = useState(defaultOpen);

  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((prev) => ({ ...prev, [activeGroupId]: true }));
    }
  }, [activeGroupId]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openLabel = tApp(t, "open", locale === "en" ? "Open" : "Ouvrir");
  const closeLabel = tApp(t, "close", locale === "en" ? "Close" : "Fermer");

  return (
    <aside className={`gsd-admin-sidebar gsd-role-sidebar karate-admin-grouped-sidebar`} data-testid={testId}>
      <div className="gsd-role-sidebar__header">
        <p className="gsd-role-sidebar__title">{title}</p>
        {subtitle ? <p className="gsd-role-sidebar__subtitle">{subtitle}</p> : null}
      </div>
      <nav className="gsd-admin-sidebar__nav karate-admin-grouped-sidebar__nav" aria-label={ariaLabel || title}>
        {groups.map((group) => {
          if (!group.collapsible) {
            return group.items.map((item) => (
              <GsdModuleNavItem
                key={item.id}
                item={item}
                isActive={isItemActive?.(item)}
                renderLink={renderLink}
                className="gsd-admin-sidebar__item"
              />
            ));
          }

          const isOpen = Boolean(openGroups[group.id] ?? group.id === activeGroupId);
          const toggleLabel = isOpen ? closeLabel : openLabel;

          return (
            <div
              key={group.id}
              className="karate-admin-grouped-sidebar__group"
              data-testid={`${groupTestIdPrefix}-${group.id}`}
              data-open={isOpen ? "true" : "false"}
            >
              <button
                type="button"
                className="karate-admin-grouped-sidebar__group-toggle"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
                data-testid={`${groupTestIdPrefix}-toggle-${group.id}`}
              >
                <span className="karate-admin-grouped-sidebar__group-icon" aria-hidden="true">
                  {group.icon}
                </span>
                <span className="karate-admin-grouped-sidebar__group-label">{group.label}</span>
                <span className="karate-admin-grouped-sidebar__chevron" aria-hidden="true">
                  {isOpen ? "▼" : "▶"}
                </span>
                <span className="visually-hidden">{toggleLabel}</span>
              </button>
              {isOpen ? (
                <div className="karate-admin-grouped-sidebar__children">
                  {group.items.map((item) => (
                    <GsdModuleNavItem
                      key={item.id}
                      item={item}
                      isActive={isItemActive?.(item)}
                      renderLink={renderLink}
                      className="gsd-admin-sidebar__item karate-admin-grouped-sidebar__child-item"
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
